import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Icon } from "../components/Icon";
import { PageHeader } from "../components/shared/PageHeader";
import { CTASection } from "../components/shared/CTASection";
import { SERVICES, SECONDARY_SERVICES, IMAGES } from "../data/content";

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader
        testId="services-header"
        eyebrow="Our Services"
        title="Advisory built for aviation's hardest questions."
        subtitle="Five core service lines covering safety, operations, AI readiness, security and government capability — each designed to strengthen your organization end to end."
        image={IMAGES.cockpit}
      />

      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-7xl space-y-20 px-5 lg:px-8 lg:space-y-28">
          {SERVICES.map((s, i) => {
            const flip = i % 2 === 1;
            return (
              <div key={s.slug} id={s.slug} className="scroll-mt-28">
                <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
                  <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
                    <Reveal>
                      <img src={IMAGES[s.image]} alt={s.title} className="h-80 w-full rounded-3xl object-cover lg:h-[440px]" />
                    </Reveal>
                  </div>
                  <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                    <Reveal delay={0.1}>
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand">
                        <Icon name={s.icon} className="h-6 w-6" />
                      </span>
                      <h2 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl">
                        {s.title}
                      </h2>
                      <p className="mt-2 text-base font-medium text-aero">{s.tagline}</p>
                      <p className="mt-5 text-base leading-relaxed text-muted-foreground">{s.summary}</p>
                      <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                        {s.features.map((f) => (
                          <li key={f} className="flex items-start gap-3 text-sm font-medium text-navy">
                            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" /> {f}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/quote"
                        data-testid={`service-quote-${s.slug}`}
                        className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-aero px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                      >
                        Request this service <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </Reveal>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-secondary py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Also available</span>
            <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Secondary service lines</h2>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {SECONDARY_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="flex h-full items-start gap-5 rounded-2xl border border-border bg-white p-8">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <Icon name={s.icon} className="h-6 w-6" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-navy">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.summary}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
